import { Component, DestroyRef, Input, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BankFeeType, BankInstrumentType } from '../../../models/bank-transaction/bank-transaction';
import { BankCounterLineFormGroup, BankFeeFormGroup } from '../../../models/bank-transaction/bank-transaction-form';
import {
  BankMovementControls,
  BankMovementMode,
  CounterAccountControls,
  PreviewLine,
  netMovementAmount,
} from '../../../models/bank-transaction/bank-movement-form';
import { PostingModule } from '../../../models/financial-account/financial-account';

export interface BankMovementOption {
  /** English fallback, shown when {@link labelKey} is absent or not yet translated. */
  label: string;
  value: number;
  /** i18n key rendered through `translateWF`, falling back to {@link label}. */
  labelKey?: string;
}

/**
 * The shared body of a bank movement screen (#5089) — instrument, money, charges and the live journal
 * entry preview. Everything below the page header.
 *
 * <p><b>Why one component rather than three.</b> A transfer leg <i>is</i> a bank movement: same amount,
 * same date, same instrument identifiers, same charge rows, same entry preview. Splitting those into
 * separate widgets left each host re-assembling the same layout and re-deriving the same rules, and they
 * had already drifted — the transfer screen was rendering an entry preview with no balance check at all.</p>
 *
 * <p>Content slots: <c>movementContext</c> above the columns, <c>movementGauge</c> and
 * <c>movementExtras</c> in the left one, and <c>movementAsideLead</c> / <c>movementAside</c> above and
 * below the entry preview on the right.</p>
 *
 * <p><b>What varies is a <see cref="BankMovementMode"/>, not a pile of booleans.</b> The four modes are
 * mutually exclusive situations, so labels, charge direction and which sections appear all follow from one
 * input; a caller cannot ask for an instrument on a receipt confirmation. Sections a mode genuinely does
 * not have are absent from <see cref="BankMovementControls"/> instead of switched off.</p>
 *
 * <p>The host keeps its own form and its own header, and projects what only it can know: the account
 * context, the zero-difference gauge, extra cards, and the action buttons.</p>
 */
@Component({
  selector: 'app-bank-movement-form',
  templateUrl: './bank-movement-form.component.html',
  styleUrl: './bank-movement-form.component.scss',
})
export class BankMovementFormComponent implements OnInit {
  readonly PostingModule = PostingModule;

  @Input({ required: true }) mode!: BankMovementMode;
  @Input({ required: true }) controls!: BankMovementControls;

  /** Currency of the account this movement hits. Shown beside every amount; never a choice here. */
  @Input() currencyLabel = '';

  /** False once the document is posted — hides Add/Remove on charges. Control disabling stays the host's. */
  @Input() editable = true;

  /**
   * The entry as it will post. Supplied by the host because only it knows the accounts involved; this
   * component owns the presentation and the balance check.
   */
  /**
   * Whether to draw the entry-as-it-will-post card.
   *
   * On by default, because most of a movement's life it has not posted yet and the preview is the only way
   * to see what it will do. Turn it off once it HAS posted and the page shows the real entry: a mimic of
   * something that already exists is at best redundant and at worst quietly wrong, since it is rebuilt from
   * the form rather than read from the ledger.
   */
  @Input() showPreview = true;

  @Input() previewLines: PreviewLine[] = [];

  /**
   * True when the preview's lines are not all in one currency — a converted internal transfer.
   *
   * <p>An explicit input rather than something derived from the lines: it decides whether the totals row
   * means anything, and adding Lempiras to dollars produces a number that is not about anything. The host
   * already knows whether its entry converts, so it says so.</p>
   */
  @Input() previewMixedCurrency = false;

  /** Shown above the preview — e.g. the entry's number once posted. */
  @Input() previewTitle = '';
  @Input() previewSubtitle = '';

  /**
   * Which instrument types this screen offers. A standalone movement includes BANK_INITIATED (the bank
   * moved the money itself); a transfer is something we initiate, so it does not.
   */
  @Input() instrumentTypeOptions: BankMovementOption[] = [];

  @Input() feeTypeOptions: BankMovementOption[] = [
    { label: 'Bank fee', value: BankFeeType.BANK },
    { label: 'Security fee', value: BankFeeType.SECURITY },
  ];

  /** Only meaningful when `controls.counterAccount` is present. */
  @Input() counterAccountRequired = false;
  @Input() counterAccountLabel = '';

  private readonly destroyRef = inject(DestroyRef);

  private readonly instrumentType = signal<number>(BankInstrumentType.BANK_INITIATED);
  private readonly amount = signal<number | null>(null);
  private readonly feeTotalSig = signal(0);
  private readonly counterTotalSig = signal(0);

  /**
   * Bumped when a breakdown row is added or removed. A computed over a FormArray cannot see its controls
   * change — `valueChanges` covers the values, nothing covers the structure — so the table would keep
   * rendering the old row set. Same device `journal-entry-manual` uses for its line table.
   */
  private readonly counterStructureVersion = signal(0);

  readonly showWireFields = computed(() => this.instrumentType() === BankInstrumentType.WIRE);
  readonly showAchFields = computed(() => this.instrumentType() === BankInstrumentType.ACH);

  readonly totalFees = computed(() => this.feeTotalSig());

  // ---- GL breakdown -----------------------------------------------------------

  /** What the breakdown currently adds up to. The number under the table, live as the user types. */
  readonly counterTotal = computed(() => this.counterTotalSig());

  /**
   * What is still unaccounted for: the gross minus what the rows name. Zero is the only value that posts.
   *
   * <p>Signed on purpose. "Over by 500" and "500 left to allocate" are different mistakes and the user
   * fixes them differently, so the figure says which rather than showing an absolute difference.</p>
   */
  readonly counterRemaining = computed(() => (this.amount() ?? 0) - this.counterTotal());

  /**
   * True when the rows add up to the movement. Mirrors the API's own check, which refuses the post — this
   * is the same rule stated early enough to fix in place rather than as a toast after submitting.
   */
  readonly counterBalanced = computed(() => Math.abs(this.counterRemaining()) < 0.005);

  /**
   * The rows, as the table's `[value]`. Reads `counterStructureVersion` so add/remove redraws it.
   */
  readonly counterLineRows = computed<FormGroup<BankCounterLineFormGroup>[]>(() => {
    this.counterStructureVersion();
    return [...(this.controls.counterAccount?.controls ?? [])];
  });

  /** No rows at all is a draft in progress, not an imbalance — it should not read as being "over" by the
   *  whole amount before the user has typed anything. */
  readonly showCounterTotals = computed(() => this.counterLineRows().length > 0);

  /** p-table hands each row back as `unknown`; this puts the type back so the cells keep their controls. */
  getCounterGroup(row: unknown): FormGroup<BankCounterLineFormGroup> {
    return row as FormGroup<BankCounterLineFormGroup>;
  }

  /**
   * What actually moves the bank balance — and on `transfer-receive`, the single number the whole step
   * exists to establish. The amount there is the gross the transfer sent, which is fixed, so what landed is
   * whatever survives the charges. Shown in every mode.
   */
  readonly netAmount = computed(() => netMovementAmount(this.mode, this.amount() ?? 0, this.totalFees()));

  readonly showNet = computed(() => true);

  readonly previewDebits = computed(() => this.previewLines.reduce((s, l) => s + l.debit, 0));
  readonly previewCredits = computed(() => this.previewLines.reduce((s, l) => s + l.credit, 0));

  /**
   * Warned about, never blocking. The preview cannot resolve the config-driven fee accounts — those are
   * named by role and looked up server-side — so a preview that reads unbalanced is a prompt to look, not
   * proof the entry is wrong. The API is the authority and its message is what surfaces on failure.
   */
  readonly previewBalanced = computed(() => this.previewLines.length > 0 && Math.abs(this.previewDebits() - this.previewCredits()) < 0.005);

  // ---- Mode-derived labels ----------------------------------------------------

  readonly moneyTitle = computed(() => {
    switch (this.mode) {
      case 'movement-in':
        return { key: 'BankTransaction_MoneyIn', fallback: 'Money in' };
      case 'movement-out':
        return { key: 'BankTransaction_MoneyOut', fallback: 'Money out' };
      case 'transfer-receive':
        return { key: 'InternalTransfer_Section_Receipt', fallback: 'Confirm what landed' };
      default:
        return { key: 'InternalTransfer_Section_Money', fallback: 'Money' };
    }
  });

  readonly amountLabel = computed(() => {
    switch (this.mode) {
      case 'transfer-send':
        return { key: 'InternalTransfer_Field_Amount', fallback: 'Amount to send' };
      case 'transfer-receive':
        return { key: 'InternalTransfer_Field_AmountSent', fallback: 'Amount sent' };
      default:
        return { key: 'BankTransaction_Details_Form_Field_Amount', fallback: 'Amount' };
    }
  });

  readonly dateLabel = computed(() => {
    switch (this.mode) {
      case 'transfer-send':
        return { key: 'InternalTransfer_Field_TransferDate', fallback: 'Transfer date' };
      case 'transfer-receive':
        return { key: 'InternalTransfer_Field_ReceivedOn', fallback: 'Receipt date' };
      default:
        return { key: 'BankTransaction_Details_Form_Field_TransactionDate', fallback: 'Date' };
    }
  });

  readonly netLabel = computed(() => {
    switch (this.mode) {
      case 'movement-in':
        return { key: 'BankTransaction_NetReceived', fallback: 'NET RECEIVED' };
      case 'transfer-send':
        return { key: 'InternalTransfer_TotalDebited', fallback: 'Leaves the source account' };
      case 'transfer-receive':
        return { key: 'InternalTransfer_TotalLanded', fallback: 'Lands in the account' };
      default:
        return { key: 'BankTransaction_NetPaid', fallback: 'TOTAL PAID' };
    }
  });

  /** The source bank's charge on a send reconciles against a different statement than the arrival's. */
  readonly statementReferenceHint = computed(() => {
    switch (this.mode) {
      case 'transfer-send':
        return { key: 'InternalTransfer_SendRef_Hint', fallback: 'As it appears on the SOURCE bank statement.' };
      case 'transfer-receive':
        return { key: 'InternalTransfer_ReceiveRef_Hint', fallback: 'As it appears on the RECEIVING bank statement.' };
      default:
        return { key: 'BankTransaction_Reference_Hint', fallback: 'What reconciliation matches against the bank statement.' };
    }
  });

  // ---- Lifecycle --------------------------------------------------------------

  ngOnInit(): void {
    this.amount.set(this.controls.amount.value);
    this.controls.amount.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v) => this.amount.set(v));

    this.syncFeeTotal();
    this.controls.fees.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.syncFeeTotal());

    const counter = this.controls.counterAccount;
    if (counter) {
      this.syncCounterTotal();
      this.counterStructureVersion.update((v) => v + 1);
      counter.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.syncCounterTotal();
        // Covers the host loading a saved movement: it clears the array and pushes a row per stored line,
        // which is a structure change this component did not make and would otherwise never see.
        this.counterStructureVersion.update((v) => v + 1);
      });
    }

    const instrument = this.controls.instrument;
    if (instrument) {
      this.applyInstrumentRules(instrument.type.value);
      instrument.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v) => this.applyInstrumentRules(v));
    }
  }

  private syncFeeTotal(): void {
    this.feeTotalSig.set(this.controls.fees.controls.reduce((sum, g) => sum + (g.controls.amount.value ?? 0), 0));
  }

  private syncCounterTotal(): void {
    const rows = this.controls.counterAccount?.controls ?? [];
    this.counterTotalSig.set(rows.reduce((sum, g) => sum + (g.controls.amount.value ?? 0), 0));
  }

  get counterRows(): CounterAccountControls | undefined {
    return this.controls.counterAccount;
  }

  /**
   * Adds a row, pre-filled with whatever is still unallocated.
   *
   * <p>The first row on a 10,000 movement therefore arrives holding 10,000, which is the common case —
   * one account for the whole thing — and the second arrives holding whatever the first left over. Typing
   * over it is the split; leaving it is the answer that already balances.</p>
   */
  addCounterLine(): void {
    const remaining = this.counterRemaining();
    this.controls.counterAccount?.push(
      new FormGroup<BankCounterLineFormGroup>({
        reference: new FormControl<string | null>(null),
        number: new FormControl<string | null>(null),
        name: new FormControl<string | null>(null),
        amount: new FormControl<number | null>(remaining > 0 ? remaining : null, [Validators.min(0.01)]),
        description: new FormControl<string | null>(null),
      }),
    );
    this.counterStructureVersion.update((v) => v + 1);
  }

  removeCounterLine(index: number): void {
    this.controls.counterAccount?.removeAt(index);
    this.counterStructureVersion.update((v) => v + 1);
  }

  // ---- Charges ----------------------------------------------------------------

  get feeRows() {
    return this.controls.fees;
  }

  addFee(): void {
    this.controls.fees.push(
      new FormGroup<BankFeeFormGroup>({
        type: new FormControl<number>(BankFeeType.BANK, { nonNullable: true }),
        amount: new FormControl<number | null>(null, [Validators.min(0.01)]),
      }),
    );
  }

  removeFee(index: number): void {
    this.controls.fees.removeAt(index);
  }

  // ---- Instrument -------------------------------------------------------------

  /**
   * Applies the per-type instrument rules, mirroring `BankInstrumentHelper.Validate` on the API.
   *
   * <p>Clearing the fields that don't belong is the half that matters. The API rejects a WIRE carrying a
   * trace number as firmly as one missing its end-to-end id, and a value stranded by switching type is
   * invisible the moment its input stops rendering — the user would see a valid-looking form and an error
   * naming a field they cannot find.</p>
   *
   * <p>Living here rather than on each host is the point of the extraction: the rule has to agree with the
   * API, and a rule copied into two screens is a rule that will eventually only agree in one of them.</p>
   */
  private applyInstrumentRules(type: number): void {
    this.instrumentType.set(type);
    const instrument = this.controls.instrument;
    if (!instrument) return;

    const isWire = type === BankInstrumentType.WIRE;
    const isAch = type === BankInstrumentType.ACH;

    if (!isWire) {
      instrument.endToEndId.setValue(null, { emitEvent: false });
      instrument.uetr.setValue(null, { emitEvent: false });
      instrument.chargeBearer.setValue(null, { emitEvent: false });
    }
    if (!isAch) instrument.traceNumber.setValue(null, { emitEvent: false });

    BankMovementFormComponent.setRequired(instrument.endToEndId, isWire);
    BankMovementFormComponent.setRequired(instrument.traceNumber, isAch);
  }

  private static setRequired(control: FormControl<string | null>, required: boolean): void {
    if (required) control.setValidators([Validators.required]);
    else control.clearValidators();
    control.updateValueAndValidity({ emitEvent: false });
  }
}
